// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  FilterOption,
  FilterToggleGroup,
} from '@/components/filter-toggle-group';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { TableControlContainer } from '@/components/table-control-container';
import {
  CheckCircle,
  DoNotDisturbOnOutlined,
  ListRounded,
  PendingOutlined,
} from '@/constants/hardfin-standard-icons';
import { CustomerTriageStatus } from '@/customer/api/triage-types';
import { CustomerTriageTable } from '@/customer/components/customer-triage-table';
import { CustomerTriageTableSkeleton } from '@/customer/components/customer-triage-table-skeleton';
import { TriageCustomerSearchListing } from '@/customer/triage-customer-search';
import { Stack } from '@mui/material';
import { Suspense } from 'react';

interface CustomerTriageListingProps {
  currentStatus: CustomerTriageStatus;
  searchParameters: TriageCustomerSearchListing;
  navigateFn: (options: { search: TriageCustomerSearchListing }) => void;
}

const STATUS_FILTER_OPTIONS: FilterOption<CustomerTriageStatus>[] = [
  {
    value: 'all',
    label: 'All',
    icon: <ListRounded fontSize="small" />,
  },
  {
    value: 'NEEDS_REVIEW',
    label: 'Needs review',
    icon: <PendingOutlined fontSize="small" />,
  },
  {
    value: 'MATCHED',
    label: 'Matched',
    icon: <CheckCircle fontSize="small" />,
  },
  {
    value: 'DISMISSED',
    label: 'Dismissed',
    icon: <DoNotDisturbOnOutlined fontSize="small" />,
  },
];

function toUrlStatus(
  status: CustomerTriageStatus
): 'all' | 'matched' | 'dismissed' | undefined {
  if (status === 'all') {
    return 'all';
  }
  if (status === 'MATCHED') {
    return 'matched';
  }
  if (status === 'DISMISSED') {
    return 'dismissed';
  }
  return undefined;
}

export function CustomerTriageListing(props: CustomerTriageListingProps) {
  const { navigateFn, searchParameters, currentStatus } = props;

  const handleStatusChange = (newStatus: CustomerTriageStatus) => {
    const { page: _, ...restParams } = searchParameters;
    navigateFn({
      search: {
        ...restParams,
        status: toUrlStatus(newStatus),
      },
    });
  };

  const handlePageChange = (page: number) => {
    navigateFn({
      search: { ...searchParameters, page },
    });
  };

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FilterToggleGroup
          value={currentStatus}
          options={STATUS_FILTER_OPTIONS}
          onChange={handleStatusChange}
          ariaLabel="customer triage status filter"
        />
      </Stack>

      <TableControlContainer>
        <Stack direction="row" marginRight="auto" />
      </TableControlContainer>

      <SupportErrorBoundary message="Failed to load triage customers">
        <Suspense fallback={<CustomerTriageTableSkeleton />}>
          <CustomerTriageTable
            filter={{
              status: currentStatus,
              page: searchParameters.page,
            }}
            onPageChange={handlePageChange}
          />
        </Suspense>
      </SupportErrorBoundary>
    </Stack>
  );
}
